import { createSlice } from '@reduxjs/toolkit';

export const LOADING_KEY = {
  FAVOURITE: 'FAVOURITE',
  DOWNLOAD: 'DOWNLOAD',
  CARD_LOADING: 'CARD_LOADING',
};

export const ADDITIONAL_DATA_KEY = {
  UPLOAD_FAILED: 'UPLOAD_FAILED',
};

export const createPaginatedSlice = (
  name,
  { reducers, defaultState, newItemPosition = 'start', pagination = 'scroll' } = {}
) => {
  const initialState = {
    data: {},
    list: [],
    prevList: [],
    searchResultList: {}, // map of filter option label id, and results
    page: 1,
    search: '',
    error: null,
    perPage: 20,
    hasData: false,
    dataLoadedOnce: false,
    loading: false,
    currentRequestId: null,
    addLoading: false,
    deleteLoading: false,
    deletedItems: [],
    filter: null,
    filterValue: [],
    refetchCounter: 0,
    triggerNextPageCounter: 0, // just ads + 1 when we want to trigger next page
    progress: {}, // tracks request progress -> { id: { total: 222, loaded: 2 }}
    itemsLoading: {}, // { [id]: { favourite: true, name: false, item: true }} // keeps state of loading of different data for an item
    additionalData: {}, // { [id]: { some data }}
    isItemUploading: false,
    ...defaultState,
  };

  const slice = createSlice({
    name: name,
    initialState,
    reducers: {
      ...reducers,
      resetState: () => {
        return initialState;
      },
      triggerFetchNextPage: (state) => {
        state.triggerNextPageCounter += 1;
      },
      setPerPage: (state, { payload }) => {
        state.perPage = payload;
        state.page = 1;
        state.refetchCounter = state.refetchCounter + 1;
      },
      setList: (state, { payload }) => {
        state.list = payload;
      },
      setSearch: (state, { payload }) => {
        state.search = payload;
      },
      setLoading: (state, { payload }) => {
        state.loading = payload;
      },
      [`fetchListStart`]: (state, { payload }) => {
        state.loading = true;
        state.currentRequestId = payload.requestId;
        state.error = null;
      },
      [`fetchListSuccess`]: (state, { payload }) => {
        if (state.currentRequestId !== payload.requestId) return;
        state.dataLoadedOnce = true;
        state.error = null;

        state.page = payload.page;
        state.data = payload.data;
        if (pagination === 'normal') {
          state.list = payload.data.results;
        } else {
          state.list = [...state.list, ...payload.data.results]; // TODO: leave only unique items in the array
        }
        state.loading = false;

        if (!state.hasData) {
          // we update has data only once to know that he has some data so if later on filters are applied we still know that data is there
          state.hasData = payload.data.count > 0;
        }
      },
      [`fetchListFailed`]: (state, { payload }) => {
        state.error = payload.error;
        state.dataLoadedOnce = true;
        state.loading = false;

        if (state.data && state.data.total_pages) {
          state.data.current_page_number = state.data.total_pages; // set current page to last page so we're not trying to load more pages
        }
      },
      [`softResetList`]: (state) => {
        state.data = {};
        state.list = [];
        state.page = 1;
        state.triggerRefetch = 0;
      },
      [`resetList`]: (state) => {
        state.prevList = state.list;
        state.data = {};
        state.list = [];
        state.page = 1;
        state.dataLoadedOnce = false;
        state.triggerRefetch = 0;
      },
      [`fullResetList`]: (state) => {
        state.data = {};
        state.list = [];
        state.page = 1;
        state.hasData = false;
        state.loading = false;
        state.currentRequestId = null;
        state.dataLoadedOnce = false;
        state.triggerRefetch = 0;
      },
      triggerRefetch: (state) => {
        state.refetchCounter = state.refetchCounter + 1;
      },

      // add
      [`addStart`]: (state, { payload }) => {
        if (payload.data?.isTemporaryItem) {
          state.list =
            newItemPosition === 'start'
              ? [payload.data, ...state.list]
              : [...state.list, payload.data];
        } else {
          state.addLoading = true;
        }
      },
      [`addSuccess`]: (state, { payload }) => {
        state.addLoading = false;
        state.hasData = true;

        if (payload.temporaryId) {
          // optimistic ui logic, item was already added we just need to update it with the data from the server
          state.list = state.list.map((item) => {
            return item.isTemporaryItem && item.id === payload.temporaryId ? payload.data : item;
          });
        } else {
          state.list =
            newItemPosition === 'start' ? [payload, ...state.list] : [...state.list, payload];
        }
      },
      [`addFailed`]: (state, { payload }) => {
        state.addLoading = false;

        if (payload.temporaryId) {
          // optimistic ui logic, item was already added we need to remove it from the list
          state.list = state.list.filter((item) => item.id !== payload.temporaryId);
        }
      },
      setAddLoading: (state, { payload }) => {
        state.addLoading = payload;
      },

      // delete
      [`deleteStart`]: (state, { payload }) => {
        if (payload.itemId) {
          const item = state.list.find((item) => item.id === payload.itemId);
          if (!item) {
            throw new Error('Item with this id does not exist');
          }

          state.list = state.list.filter((item) => item.id !== payload.itemId);
          state.deletedItems.push(item);
        }
        state.deleteLoading = true;
      },
      [`deleteSuccess`]: (state, { payload }) => {
        state.deleteLoading = false;
        state.deletedItems = state.deletedItems.filter((item) => item.id !== payload.itemId);
        state.list = state.list.filter((item) => item.id !== payload.itemId);
      },
      [`deleteFailed`]: (state, { payload }) => {
        state.deleteLoading = false;

        if (payload.itemId) {
          // optimistic ui logic
          const item = state.deletedItems.find((item) => item.id === payload.itemId);
          if (!item) return;
          state.list = [item, ...state.list];
        }
      },

      // update
      [`update`]: (state, { payload }) => {
        state.list = state.list.map((item) => {
          if (item.id === payload.id) {
            return {
              ...item,
              ...payload,
            };
          }

          return item;
        });
      },
      [`updateAll`]: (state, { payload }) => {
        state.list = state.list.reduce((acc, curr) => {
          const itemExists = payload.find(({ id }) => id === curr.id);
          if (itemExists) {
            acc.push({ ...itemExists });
          } else {
            acc.push(curr);
          }

          return acc;
        }, []);
      },
      setItemLoading: (state, { payload }) => {
        const { id, key, value } = payload;
        if (!state.itemsLoading[id]) {
          state.itemsLoading[id] = {};
        }

        state.itemsLoading[id][key] = value;
      },

      setProgress: (state, { payload }) => {
        const { id, loaded, total } = payload;
        if (!state.progress[id]) {
          state.progress[id] = {};
        }

        state.progress[id].total = total;
        state.progress[id].loaded = loaded;

        state.progress[id].completed = total === loaded;

        const notCompletedItem = Object.values(state.progress).find((p) => p.completed === false);
        if (!notCompletedItem) {
          // reset progress state after all items are uploaded so when user uploads next time he sees upload from 0
          state.progress = {};
        }
      },

      resetProgress: (state) => {
        state.progress = {};
      },

      setAdditionalData: (state, { payload }) => {
        const { id, key, value } = payload;
        if (!state.additionalData[id]) {
          state.additionalData[id] = {};
        }

        state.additionalData[id][key] = value;
      },

      // filter
      setFilter: (state, { payload }) => {
        // if (!state.filter) {
        state.filter = payload;
        // }
      },
      toggleFilter: (state, { payload }) => {
        state.filterValue = state.filterValue.includes(payload)
          ? state.filterValue.filter((x) => x !== payload)
          : [...state.filterValue, payload];
      },
      setFilterValue: (state, { payload }) => {
        state.filterValue = payload;
      },
      groupFilterUpdate: (state, { payload }) => {
        let newValue = [...state.filterValue];
        payload.forEach(({ name, enabled }) => {
          if (enabled && !newValue.includes(name)) {
            newValue.push(name);
            return;
          }

          if (!enabled && newValue.includes(name)) {
            newValue = newValue.filter((v) => v !== name);
          }
        });

        state.filterValue = newValue;
      },
      resetFilters: (state) => {
        state.filterValue = [];
      },

      // search
      searchStart: (state, { payload }) => {
        // state.loading = true;
        state.searchResultList[payload.optionId] = {
          currentRequestId: payload.requestId,
          list: [],
          loading: true,
        };
      },
      searchSuccess: (state, { payload }) => {
        if (state.searchResultList[payload.optionId].currentRequestId !== payload.requestId) return;

        state.searchResultList[payload.optionId] = {
          list: payload.data.results,
          loading: false,
        };
      },
      searchFailed: (state, { payload }) => {
        if (state.currentRequestId !== payload.requestId) return;
        state.searchResultList[payload.optionId] = {
          list: [],
          loading: false,
        };
      },
      setIsItemUploading: (state, { payload }) => {
        state.isItemUploading = payload;
      },
    },
  });

  slice.LF = {
    selectors: {
      selectIsFavouriteLoading: (state, id) =>
        Boolean(state[name]?.itemsLoading?.[id]?.[LOADING_KEY.FAVOURITE]),
      selectIsCardLoading: (state, id) =>
        Boolean(state[name].itemsLoading?.[id]?.[LOADING_KEY.CARD_LOADING]),
      selectIsDownloadLoading: (state, id) =>
        Boolean(state[name].itemsLoading?.[id]?.[LOADING_KEY.DOWNLOAD]),
      selectProgress: (state) => state[name].progress,
      selectItemProgress: (state, id) => state[name].progress[id],
      selectItemsLoading: (state) => state[name].itemsLoading,
      selectItemUploadFailed: (state, id) =>
        state[name].additionalData?.[id]?.[ADDITIONAL_DATA_KEY.UPLOAD_FAILED],
      selectList: (state) => state[name].list,
      selectTotal: (state) => state[name].data?.count,
      selectLoading: (state) => state[name].loading,
      selectFilterBrandList: (state) => state[name].filter?.brands || [],
      selectSearch: (state) => state[name].search,
      selectItem: (state, id) => state[name].list.find((item) => item.id === id),

      selectAllFailedUpload: (state) => {
        // TODO: add memo to this selector as currently every time it'll return new array
        const result = Object.entries(state[name].additionalData)
          .map(([id, data]) => {
            return data[ADDITIONAL_DATA_KEY.UPLOAD_FAILED];
          })
          .filter(Boolean);

        return result.length === 0 ? null : result;
      },
      
      selectIsItemUploading: (state) => state[name].isItemUploading,
    },
  };

  return slice;
};

export const sar =
  (propertyName) =>
  (state, { payload }) => {
    state[propertyName] = payload;
  };

export const updateObject =
  (propertyName) =>
  (state, { payload }) => {
    state[propertyName] = {
      ...state[propertyName],
      ...payload,
    };
  };

export const updateOneItemInList =
  (propertyName) =>
  (state, { payload }) => {
    state[propertyName] = state[propertyName].map((a) => {
      if (a.id === payload.id) {
        return {
          ...a,
          ...payload,
        };
      }
      return a;
    });
  };

export const updateItemsInList =
  (propertyName, payloadKey) =>
  (state, { payload }) => {
    const updatedItems = payloadKey ? payload[payloadKey] : payload;

    state[propertyName] = state[propertyName].map((s) => {
      const updatedItem = updatedItems.find((item) => item.id === s.id);
      return updatedItem ? { ...s, ...updatedItem } : s;
    });
  };

export const addItemToList =
  (propertyName) =>
  (state, { payload }) => {
    state[propertyName].push(payload);
  };


  export const addItemsToList =
  (propertyName) =>
  (state, { payload }) => {
    state[propertyName] = [...state[propertyName], ...payload];
  };

export const deleteListItem =
  (propertyName) =>
  (state, { payload }) => {
    state[propertyName] = state[propertyName].filter((c) => c.id !== payload);
  };

export const updateItemInMapList =
  (propertyName, idKey, itemKey, beforeId) =>
  (state, { payload }) => {
    const id = payload[idKey];
    const updatedItem = payload[itemKey];

    const baseState = beforeId ? state[beforeId] : state;

    baseState[id][propertyName] = baseState[id][propertyName].map((a) => {
      return a.id === updatedItem.id ? { ...a, ...updatedItem } : a;
    });
  };

export const addItemsToMapList =
  (propertyName, idKey, itemKey, beforeId) =>
  (state, { payload }) => {
    const id = payload[idKey];
    const items = payload[itemKey];

    const baseState = beforeId ? state[beforeId] : state;

    baseState[id][propertyName] = [...baseState[id][propertyName], ...items];
  };

  export const removeItemsFromMapList =
  (propertyName, idKey, beforeId, idsKey = 'ids') =>
  (state, { payload }) => {
    const id = payload[idKey];
    const idsToremove = payload[idsKey];

    const baseState = beforeId ? state[beforeId] : state;
    baseState[id][propertyName] = baseState[id][propertyName].filter(item => !idsToremove.includes(item.id));
  };


  export const updateItemsInMapList =
  (propertyName, idKey, itemKey, beforeId) =>
  (state, { payload }) => {
    const id = payload[idKey];
    const updatedItems = payload[itemKey];

    const baseState = beforeId ? state[beforeId] : state;

    baseState[id][propertyName] = baseState[id][propertyName].map((a) => {
      const updatedItem = updatedItems.find((item) => item.id === a.id);
      return updatedItem ? { ...a, ...updatedItem } : a;
    });
  };